// EDITOR domain: flows, versions, nodes, edges, groups, node catalog (backend §12–24).
import { db, byId, eq, sortBy, insert, update, whenPersisted, removeLocal } from './core';

export const getFlow = (id?: any) => byId('flows', id);
export const projectFlows = (projectId?: any) => sortBy(eq('flows', 'project_id', projectId), 'updated_at');
export async function deleteFlow(id: string) {
  const response = await fetch(`/api/v1/flows/${encodeURIComponent(id)}`, { method:'DELETE' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not delete flow (${response.status})`);
  removeLocal('flows', id);
  window.dispatchEvent(new CustomEvent('cf:data-updated', { detail:'flows' }));
}
export const flowVersions = (flowId?: any) => sortBy(eq('flow_versions', 'flow_id', flowId), 'version');
export const getNodeType = (id?: any) => byId('node_types', id);
export const listNodeTypes = () => db.node_types;

export function flowGraph(flowId?: any) {
  const flow = getFlow(flowId);
  const nodes = eq('nodes', 'flow_id', flowId).map((n?: any) => ({ ...n, type: byId('node_types', n.node_type_id) }));
  const edges = eq('edges', 'flow_id', flowId);
  const groups = eq('node_groups', 'flow_id', flowId).map((g?: any) => ({ ...g, node_ids: eq('node_group_members', 'group_id', g.id).map((m?: any) => m.node_id) }));
  return { flow, nodes, edges, groups, draft: byId('flow_versions', flow.current_draft_version_id), published: byId('flow_versions', flow.published_version_id) };
}

export const lastRunOfFlow = (flowId?: any) => eq('flow_last_runs', 'flow_id', flowId)[0] || null;

// Execution scope → ordered node ids (single execution API for all run modes, backend §49–51)
export function executionPlan(flowId?: any, scope: any = { type: 'full_flow' }) {
  const { nodes, edges } = flowGraph(flowId);
  const children = (id?: any) => edges.filter((e?: any) => e.source_node_id === id).map((e?: any) => e.target_node_id);
  const parents = (id?: any) => edges.filter((e?: any) => e.target_node_id === id).map((e?: any) => e.source_node_id);
  let ids = nodes.map((n?: any) => n.id);
  if (scope.type === 'single_node' || scope.type === 'selected_nodes' || scope.type === 'group') ids = scope.node_ids;
  if (scope.type === 'run_from_here') {
    const out = new Set(scope.node_ids);
    const walk = (id?: any) => children(id).forEach((c?: any) => { if (!out.has(c)) { out.add(c); walk(c); } });
    scope.node_ids.forEach(walk);
    ids = [...out];
  }
  // topological levels → parallel batches
  const set = new Set(ids);
  const levels = [];
  const done = new Set();
  while (done.size < set.size) {
    const batch = [...set].filter((id?: any) => !done.has(id) && parents(id).filter((p?: any) => set.has(p)).every((p?: any) => done.has(p)));
    if (!batch.length) throw new Error('CYCLE_DETECTED');
    batch.forEach((id?: any) => done.add(id));
    levels.push(batch);
  }
  return levels;
}

// Validation before Run/Publish (backend §85)
export function validateFlow(flowId?: any) {
  const { nodes, edges } = flowGraph(flowId);
  const issues = [];
  nodes.forEach((n?: any) => {
    Object.entries(n.type.config_schema).forEach(([k, s]: any) => {
      if (s.required && (n.config[k] == null && !(s.type === 'asset' && n.config.asset_refs?.length))) issues.push({ node_id: n.id, code: 'CONFIG_ERROR', message: `${n.name}: “${k}” is required` });
    });
    if (n.config.connection_id) {
      const c = byId('connections', n.config.connection_id);
      if (!c || c.status === 'expired') issues.push({ node_id: n.id, code: 'CONNECTION_ERROR', message: `${n.name}: connection “${c?.name || n.config.connection_id}” is ${c ? c.status : 'missing'}` });
    }
    (n.config.asset_refs || []).forEach((r?: any) => {
      const a = byId('assets', r.asset_id);
      if (!a || a.status === 'Archived') issues.push({ node_id: n.id, code: 'VALIDATION_ERROR', message: `${n.name}: referenced asset is archived` });
    });
  });
  try { executionPlan(flowId); } catch { issues.push({ code: 'VALIDATION_ERROR', message: 'Cycle detected' }); }
  return { ok: !issues.length, issues, counts: { nodes: nodes.length, edges: edges.length } };
}

export function publishFlow(flowId?: any, userId?: any) {
  const flow = getFlow(flowId);
  const draft = byId('flow_versions', flow.current_draft_version_id);
  update('flow_versions', draft.id, { status: 'published', published_at: new Date().toISOString() });
  if (flow.published_version_id) update('flow_versions', flow.published_version_id, { status: 'deprecated' });
  const next = insert('flow_versions', { id: `${flowId}_v${draft.version + 1}`, flow_id: flowId, version: draft.version + 1, status: 'draft', created_by: userId, created_at: new Date().toISOString() });
  update('flows', flowId, { status: 'published', published_version_id: draft.id, current_draft_version_id: next.id, updated_at: new Date().toISOString() });
  insert('audit_events', { id: 'aud_' + Date.now(), organization_id: flow.organization_id, user_id: userId, action: 'flow.published', entity_type: 'flow', entity_id: flowId, after: { version: draft.version }, created_at: new Date().toISOString() });
  return draft.version;
}

export function setNodeAsset(nodeId?: any, assetId?: any) {
  const n = byId('nodes', nodeId);
  const refs = [{ asset_id: assetId, version: 'latest' }];
  update('nodes', nodeId, { config: { ...n.config, asset_refs: refs } });
  db.asset_usages = db.asset_usages.filter((u?: any) => u.node_id !== nodeId);
  insert('asset_usages', { id: 'aus_' + Date.now(), asset_id: assetId, project_id: byId('flows', n.flow_id).project_id, flow_id: n.flow_id, node_id: nodeId, usage_type: 'latest', version_ref: 'latest', created_at: new Date().toISOString() });
}

// Build a new draft flow from a linear chain of node types: [{ name?, type_key }].
// Used by blank/Executions templates and by Marketplace purchases (whose templates carry the seller's chain).
export function createFlowFromChain(projectId?: any, name?: any, chain?: any, userId?: any, { description = '', templateOriginId = null }: any = {}) {
  const p = byId('projects', projectId);
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const flow = insert('flows', { id, organization_id: p.organization_id, workspace_id: p.workspace_id, project_id: projectId, name, description, status: 'draft', template_origin_id: templateOriginId, created_by: userId, created_at: now, updated_at: now });
  let prev: string | null = null;
  const nodes: any[] = [], edges: any[] = [];
  (chain || []).forEach((step?: any, i?: any) => {
    const nid = `${id}_n${i + 1}`;
    nodes.push({ id:nid, type:step.type_key || 'unknown', x:80 + i * 270, y:160, params:{}, metadata:{ name:step.name || step.type_key } });
    if (prev) edges.push({ id:`${nid}_e`, from:{ nodeId:prev, portId:'out' }, to:{ nodeId:nid, portId:'in' } });
    prev = nid;
  });
  const graph: any = { schemaVersion:2, app:'bnb-nodes', catalogVersion:1, viewport:{ x:40, y:40, zoom:1 }, nodes, edges, groups:[], annotations:{ frames:[] } };
  void whenPersisted(flow).then(async () => {
    const response = await fetch(`/api/v1/flows/${id}/draft`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ expectedRevision:0, graph }) });
    if (!response.ok) { const body = await response.json().catch(() => ({})); window.dispatchEvent(new CustomEvent('cf:persistence-error', { detail:body.error || 'Initial graph was not saved' })); }
  }).catch((): void => undefined);
  return id;
}

import nodeTypes from '../../../../packages/canvas-runtime/nodes/node-types.json';
const supportedNodeTypes = new Set(nodeTypes);

export function validateFlowTemplate(templateAsset: any): string[] {
  const chain = templateAsset?.metadata?.chain;
  if (!Array.isArray(chain) || !chain.length) return ['Template has no node chain'];
  return [...new Set(chain.map((step: any) => String(step?.type_key || '')).filter(type => !supportedNodeTypes.has(type)))];
}

export function createFlowFromTemplate(projectId?: any, templateAsset?: any, userId?: any) {
  if (templateAsset) {
    const unsupported = validateFlowTemplate(templateAsset);
    if (unsupported.length) throw new Error(`Template uses unsupported nodes: ${unsupported.join(', ')}`);
  }
  return createFlowFromChain(
    projectId,
    templateAsset ? templateAsset.name.replace(/ Template$/, '') : 'New Flow',
    templateAsset?.metadata?.chain || [],
    userId,
    { description: templateAsset ? `Created from ${templateAsset.name}` : '', templateOriginId: templateAsset?.id || null },
  );
}

// ── canvas edits (in-memory; backend: PATCH /api/nodes/:id on the draft version) ──
export const getNode = (id?: any) => byId('nodes', id);
export const moveNode = (id?: any, x?: any, y?: any) => update('nodes', id, { position_x: Math.round(x), position_y: Math.round(y) });
export const renameNode = (id?: any, name?: any) => update('nodes', id, { name });
export function setNodeConfig(id?: any, key?: any, value?: any) { const n = byId('nodes', id); n.config[key] = value; n.updated_at = new Date().toISOString(); }
export function toggleNodeDisabled(id?: any, value?: any) { const n = byId('nodes', id); n.disabled = value ?? !n.disabled; return n.disabled; }
export function setAssetRefVersion(nodeId?: any, assetId?: any, version?: any) {
  const n = byId('nodes', nodeId);
  n.config.asset_refs = (n.config.asset_refs || []).map((r?: any) => (r.asset_id === assetId ? { ...r, version } : r));
  db.asset_usages.filter((u?: any) => u.node_id === nodeId && u.asset_id === assetId).forEach((u?: any) => { u.version_ref = version; u.usage_type = version === 'latest' ? 'latest' : 'pinned'; });
}
export function duplicateNode(id?: any, dx: any = 40, dy: any = 150) {
  const n = byId('nodes', id);
  const copy: any = { ...n, id: n.id + '_copy_' + Date.now().toString(36), name: n.name + ' copy', position_x: n.position_x + dx, position_y: n.position_y + dy, config: JSON.parse(JSON.stringify(n.config)) };
  return insert('nodes', copy);
}

export const NODE_CATEGORIES =['Input', 'Generation', 'Transform', 'Logic', 'AI', 'Media', 'Data', 'Output', 'Integration'];
